import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Calendar,
  Loader2,
  Plus,
  Factory,
  Box,
  Clock,
  Zap,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Palette,
  Package,
  Users,
  GripVertical,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Download,
  Settings,
  Move,
  RefreshCw,
  Play,
  Pause,
  AlertCircle,
} from "lucide-react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a6fd91e7-1ad0-4cf0-91ce-b769f9bab27a/artifacts/m0y29wqk_Sem%20T%C3%ADtulo.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Material order for optimal sequence (reduce purges)
const MATERIAL_ORDER = ['PP', 'PE', 'HDPE', 'LDPE', 'PS', 'ABS', 'PA', 'PC', 'POM', 'PVC', 'PET', 'TPE'];

// Color order (light to dark)
const COLOR_ORDER = ['natural', 'branco', 'amarelo', 'laranja', 'vermelho', 'verde', 'azul', 'cinza', 'castanho', 'preto'];

const WEEKDAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

// Draggable Order Card Component
function DraggableOrderCard({ order, part, hours, onRemove, machineId, dayIndex }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${machineId}-${dayIndex}-${order.id}`,
    data: { order, machineId, dayIndex, orderId: order.id }
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: 1000,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  // Verificar atraso
  const isDelayed = () => {
    if (!order.planned_end) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const plannedEnd = new Date(order.planned_end);
    return today > plannedEnd && !['completed', 'archived'].includes(order.status);
  };

  const getDelayDays = () => {
    if (!order.planned_end) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const plannedEnd = new Date(order.planned_end);
    const diff = today - plannedEnd;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const delayed = isDelayed();
  const delayDays = getDelayDays();

  // Cores baseadas no estado
  const getStatusColor = () => {
    if (delayed) return 'border-red-500 bg-red-50';
    if (order.status === 'running') return 'border-green-500 bg-green-50';
    if (order.status === 'paused') return 'border-amber-500 bg-amber-50';
    return 'border-gray-200 bg-white';
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`rounded-lg p-2 shadow-sm border-2 group relative ${getStatusColor()} ${isDragging ? 'ring-2 ring-blue-500' : ''}`}
    >
      {/* Indicador de atraso */}
      {delayed && (
        <div className="absolute -top-2 -left-2 bg-red-600 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold animate-pulse">
          -{delayDays}d
        </div>
      )}
      
      {/* Indicador de estado */}
      <div className="absolute top-1 right-6">
        {order.status === 'running' && (
          <Play className="h-3 w-3 text-green-600 fill-green-600" title="A produzir" />
        )}
        {order.status === 'paused' && (
          <Pause className="h-3 w-3 text-amber-600 fill-amber-600" title="Em pausa" />
        )}
      </div>
      
      <div 
        {...listeners} 
        {...attributes}
        className="absolute top-1 left-1 cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-gray-100"
        title="Arrastar para mover"
      >
        <GripVertical className="h-3 w-3 text-gray-400" />
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Trash2 className="h-3 w-3" />
      </button>
      <div className={`font-mono font-bold text-xs ml-4 ${delayed ? 'text-red-600' : 'text-blue-600'}`}>
        {order.code}
      </div>
      <div className="text-xs text-gray-600 truncate ml-4">
        {part?.description || '-'}
      </div>
      <div className="flex items-center justify-between mt-1 ml-4">
        <Badge variant="outline" className="text-[10px] px-1">
          {part?.material || '-'}
        </Badge>
        <span className="text-[10px] text-gray-500">
          {hours.toFixed(1)}h
        </span>
      </div>
      {delayed && (
        <div className="text-[9px] text-red-600 font-bold ml-4 mt-1 flex items-center gap-1">
          <AlertCircle className="h-2.5 w-2.5" />
          ATRASADA {delayDays} dia(s)
        </div>
      )}
    </div>
  );
}

// Droppable Cell Component
function DroppableCell({ machineId, dayIndex, children, isOver, totalHours }) {
  const { setNodeRef } = useDroppable({
    id: `${machineId}-${dayIndex}`,
    data: { machineId, dayIndex }
  });

  const getLoadColor = (hours) => {
    if (hours === 0) return 'bg-gray-50';
    if (hours < 6) return 'bg-green-50 border-green-200';
    if (hours < 10) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <td 
      ref={setNodeRef}
      className={`p-2 border-r min-h-[120px] align-top transition-all duration-200 ${getLoadColor(totalHours)} ${isOver ? 'ring-2 ring-blue-500 ring-inset bg-blue-50' : ''}`}
    >
      {children}
    </td>
  );
}

export default function Planeamento() {
  const [loading, setLoading] = useState(true);
  const [machines, setMachines] = useState([]);
  const [parts, setParts] = useState([]);
  const [molds, setMolds] = useState([]);
  const [orders, setOrders] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [users, setUsers] = useState([]);
  const [plannedOrders, setPlannedOrders] = useState({});
  const [selectedWeek, setSelectedWeek] = useState(getWeekStart(new Date()));
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [monthlyPlanning, setMonthlyPlanning] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [factoryStats, setFactoryStats] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [activeDragData, setActiveDragData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [nextRefresh, setNextRefresh] = useState(60);
  const [delayedOrders, setDelayedOrders] = useState([]);
  const [delayDialogOpen, setDelayDialogOpen] = useState(false);
  const [checkingDelays, setCheckingDelays] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor)
  );

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  function getWeekDates(startDate) {
    const dates = [];
    for (let i = 0; i < 6; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }
    return dates;
  }

  function formatDate(date) {
    return date.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
  }

  // Monthly planning functions
  function getMonthDays(date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Start from Monday of the first week
    const startDay = new Date(firstDay);
    const dayOfWeek = startDay.getDay();
    startDay.setDate(startDay.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    
    // Generate all days until we cover the whole month
    const current = new Date(startDay);
    while (current <= lastDay || current.getDay() !== 1) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
      if (days.length > 42) break; // Max 6 weeks
    }
    
    return days;
  }

  function getWeeksInMonth(days) {
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks.filter(week => week.length === 7);
  }

  function formatMonthYear(date) {
    return date.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
  }

  // Get orders for a specific date based on planned_start and estimated_days
  function getOrdersForDate(date, machineId) {
    const dateStr = date.toISOString().split('T')[0];
    return orders.filter(order => {
      if (order.machine_id !== machineId) return false;
      if (!order.planned_start) return false;
      
      const startDate = new Date(order.planned_start);
      const endDate = order.planned_end ? new Date(order.planned_end) : new Date(startDate);
      endDate.setDate(endDate.getDate() + (order.estimated_days || 1) - 1);
      
      const checkDate = new Date(dateStr);
      return checkDate >= startDate && checkDate <= endDate;
    });
  }

  // Fetch monthly planning data
  const fetchMonthlyPlanning = useCallback(async () => {
    try {
      const monthDays = getMonthDays(selectedMonth);
      const weeks = getWeeksInMonth(monthDays);
      const allPlanning = {};
      
      for (const week of weeks) {
        const monday = week.find(d => d && d.getDay() === 1) || week.find(d => d) || week[0];
        if (!monday) continue;
        
        const weekStart = monday.toISOString().split('T')[0];
        try {
          const res = await axios.get(`${API}/planning/${weekStart}`);
          if (res.data.planning) {
            Object.entries(res.data.planning).forEach(([key, value]) => {
              // A chave é "machineId-dayIndex", onde machineId é um UUID
              // Precisamos separar o dayIndex (último elemento) do machineId (resto)
              const lastDashIdx = key.lastIndexOf('-');
              const machineId = key.substring(0, lastDashIdx);
              const dayIdx = parseInt(key.substring(lastDashIdx + 1));
              
              const dayDate = new Date(monday);
              dayDate.setDate(dayDate.getDate() + dayIdx);
              const dateKey = `${machineId}-${dayDate.toISOString().split('T')[0]}`;
              allPlanning[dateKey] = value;
            });
          }
        } catch (e) {
          // Week not found, continue
        }
      }
      
      setMonthlyPlanning(allPlanning);
    } catch (error) {
      console.error("Error fetching monthly planning:", error);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchMonthlyPlanning();
  }, [fetchMonthlyPlanning]);

  const fetchData = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setIsRefreshing(true);
      const [machinesRes, partsRes, moldsRes, ordersRes, materialsRes, usersRes] = await Promise.all([
        axios.get(`${API}/machines`),
        axios.get(`${API}/parts`),
        axios.get(`${API}/molds`),
        axios.get(`${API}/orders`),
        axios.get(`${API}/raw-materials`),
        axios.get(`${API}/users`),
      ]);

      setMachines(machinesRes.data);
      setParts(partsRes.data);
      setMolds(moldsRes.data);
      // Mostrar todas as ordens não concluídas/entregues no planeamento
      setOrders(ordersRes.data.filter(o => !['completed', 'cancelled', 'archived', 'delivered'].includes(o.status)));
      setRawMaterials(materialsRes.data);
      setUsers(usersRes.data);

      // Calculate factory stats
      const operators = usersRes.data.filter(u => u.role === 'operator');
      const activeMachines = machinesRes.data.filter(m => m.status !== 'stopped');
      
      setFactoryStats({
        totalMachines: machinesRes.data.length,
        activeMachines: activeMachines.length,
        totalOperators: operators.length,
        pendingOrders: ordersRes.data.filter(o => o.status === 'pending').length,
        runningOrders: ordersRes.data.filter(o => o.status === 'running').length,
        totalStock: materialsRes.data.reduce((sum, m) => sum + (m.quantity_available || m.quantity_kg || 0), 0),
      });

      // Load saved planning for the week
      try {
        const planRes = await axios.get(`${API}/planning/${selectedWeek.toISOString().split('T')[0]}`);
        setPlannedOrders(planRes.data.planning || {});
      } catch (e) {
        setPlannedOrders({});
      }
      
      // Load scheduler status
      try {
        const token = localStorage.getItem('mes_token');
        const schedulerRes = await axios.get(`${API}/planning/scheduler-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSchedulerStatus(schedulerRes.data);
      } catch (e) {
        // Scheduler status is optional
      }
      
      setNextRefresh(60);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [selectedWeek]);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 60 seconds to keep server awake
    const refreshInterval = setInterval(() => fetchData(true), 60 * 1000);
    
    // Countdown timer
    const countdownInterval = setInterval(() => {
      setNextRefresh(prev => prev > 0 ? prev - 1 : 60);
    }, 1000);
    
    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, [fetchData]);

  const getPartById = (id) => parts.find(p => p.id === id);
  const getMoldById = (id) => molds.find(m => m.id === id);
  const getMachineById = (id) => machines.find(m => m.id === id);

  const calculateOrderTime = (order) => {
    // Retorna as horas de trabalho por dia (não o total)
    // Se a ordem tem hours_per_day definido, usa esse valor
    // Caso contrário, calcula baseado no tempo total e dias estimados
    if (order.hours_per_day) {
      return order.hours_per_day;
    }
    
    // Fallback: calcular baseado no tempo total
    const mold = getMoldById(order.mold_id);
    if (!mold) return 8; // Default 8h se não encontrar molde
    
    const cycles = Math.ceil(order.good_quantity_target / mold.cavities);
    const totalTimeHours = (cycles * mold.cycle_time) / 3600;
    
    // Dividir pelo número de dias estimados
    const estimatedDays = order.estimated_days || 1;
    return totalTimeHours / estimatedDays;
  };

  const getMaterialPriority = (material) => {
    const idx = MATERIAL_ORDER.indexOf(material?.toUpperCase());
    return idx >= 0 ? idx : 999;
  };

  const getColorPriority = (color) => {
    const idx = COLOR_ORDER.indexOf(color?.toLowerCase());
    return idx >= 0 ? idx : 999;
  };

  // Sort orders by material and color for optimal sequence
  const getSortedOrders = () => {
    return [...orders].sort((a, b) => {
      const partA = getPartById(a.part_id);
      const partB = getPartById(b.part_id);
      
      // First by material
      const matDiff = getMaterialPriority(partA?.material) - getMaterialPriority(partB?.material);
      if (matDiff !== 0) return matDiff;
      
      // Then by color (assuming color is stored in part)
      const colorDiff = getColorPriority(partA?.color) - getColorPriority(partB?.color);
      return colorDiff;
    });
  };

  const handleAddToPlanning = (machineId, dayIndex, orderId) => {
    const key = `${machineId}-${dayIndex}`;
    setPlannedOrders(prev => ({
      ...prev,
      [key]: [...(prev[key] || []), orderId]
    }));
    setDialogOpen(false);
  };

  const handleRemoveFromPlanning = (machineId, dayIndex, orderId) => {
    const key = `${machineId}-${dayIndex}`;
    setPlannedOrders(prev => ({
      ...prev,
      [key]: (prev[key] || []).filter(id => id !== orderId)
    }));
  };

  // Drag & Drop handlers
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    setActiveDragData(active.data.current);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveDragData(null);

    if (!over) return;

    const activeData = active.data.current;
    const overId = over.id;
    
    // Parse the over target (format: machineId-dayIndex)
    const [targetMachineId, targetDayIndex] = overId.split('-').slice(0, 2);
    
    if (!targetMachineId || targetDayIndex === undefined) return;

    const sourceMachineId = activeData.machineId;
    const sourceDayIndex = activeData.dayIndex;
    const orderId = activeData.orderId;

    // If dropped on same cell, do nothing
    if (sourceMachineId === targetMachineId && sourceDayIndex === parseInt(targetDayIndex)) {
      return;
    }

    // Remove from source and add to target
    setPlannedOrders(prev => {
      const sourceKey = `${sourceMachineId}-${sourceDayIndex}`;
      const targetKey = `${targetMachineId}-${targetDayIndex}`;
      
      const newPlanning = { ...prev };
      
      // Remove from source
      newPlanning[sourceKey] = (prev[sourceKey] || []).filter(id => id !== orderId);
      
      // Add to target (if not already there)
      if (!(prev[targetKey] || []).includes(orderId)) {
        newPlanning[targetKey] = [...(prev[targetKey] || []), orderId];
      }
      
      return newPlanning;
    });

    toast.success("Ordem movida com sucesso!");
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveDragData(null);
  };

  const savePlanning = async () => {
    try {
      await axios.post(`${API}/planning`, {
        week_start: selectedWeek.toISOString().split('T')[0],
        planning: plannedOrders
      });
      toast.success("Planeamento guardado!");
    } catch (error) {
      toast.error("Erro ao guardar planeamento");
    }
  };

  // Função para verificar e propagar atrasos
  const checkDelays = async () => {
    try {
      setCheckingDelays(true);
      const token = localStorage.getItem('mes_token');
      const res = await axios.post(`${API}/planning/check-delays`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.delayed_orders > 0) {
        setDelayedOrders(res.data.details || []);
        setDelayDialogOpen(true);
        toast.warning(`${res.data.delayed_orders} ordem(s) atrasada(s) encontrada(s)!`);
      } else {
        toast.success("Não há ordens atrasadas!");
      }
      
      // Recarregar dados para refletir as mudanças
      await fetchData();
    } catch (error) {
      console.error("Error checking delays:", error);
      toast.error("Erro ao verificar atrasos");
    } finally {
      setCheckingDelays(false);
    }
  };

  const autoPlanAll = async () => {
    try {
      const token = localStorage.getItem('mes_token');
      const res = await axios.post(`${API}/planning/auto-plan-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || "Planeamento automatico concluido!");
      await fetchData();
    } catch (error) {
      console.error("Error auto-planning:", error);
      toast.error("Erro no planeamento automatico");
    }
  };

  // Verificar se uma ordem está atrasada
  const isOrderDelayed = (order) => {
    if (!order.planned_end) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const plannedEnd = new Date(order.planned_end);
    return today > plannedEnd && !['completed', 'archived'].includes(order.status);
  };

  // Obter dias de atraso
  const getDelayDays = (order) => {
    if (!order.planned_end) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const plannedEnd = new Date(order.planned_end);
    const diff = today - plannedEnd;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const calculateDayLoad = (machineId, dayIndex) => {
    const key = `${machineId}-${dayIndex}`;
    const dayOrders = plannedOrders[key] || [];
    let totalHours = 0;
    
    dayOrders.forEach(orderId => {
      const order = orders.find(o => o.id === orderId);
      if (order) {
        totalHours += calculateOrderTime(order);
      }
    });
    
    return totalHours;
  };

  const getLoadColor = (hours) => {
    if (hours === 0) return 'bg-gray-100';
    if (hours < 6) return 'bg-green-100 border-green-300';
    if (hours < 10) return 'bg-amber-100 border-amber-300';
    return 'bg-red-100 border-red-300';
  };

  const weekDates = getWeekDates(selectedWeek);

  const exportToPDF = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Planeamento Semanal</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; font-size: 11px; }
          .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 3px solid #2563eb; padding-bottom: 15px; }
          .header-left { display: flex; align-items: center; gap: 15px; }
          .logo { height: 50px; }
          .header h1 { font-size: 18px; color: #2563eb; }
          .header-right { text-align: right; }
          .header-right p { color: #64748b; font-size: 11px; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: left; vertical-align: top; }
          th { background: #f1f5f9; font-weight: 600; }
          .machine-header { background: #2563eb; color: white; }
          .order-item { background: #f8fafc; padding: 5px; margin: 2px 0; border-radius: 4px; border-left: 3px solid #2563eb; }
          .hours { font-size: 10px; color: #64748b; }
          .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #94a3b8; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <img src="${LOGO_URL}" alt="Logo" class="logo" />
            <h1>Planeamento Semanal</h1>
          </div>
          <div class="header-right">
            <p><strong>Semana:</strong> ${formatDate(weekDates[0])} a ${formatDate(weekDates[5])}</p>
            <p>Gerado: ${new Date().toLocaleString("pt-PT")}</p>
          </div>
        </div>
        <table>
          <tr>
            <th>Máquina</th>
            ${weekDates.map(d => `<th>${WEEKDAYS[d.getDay() - 1]}<br/>${formatDate(d)}</th>`).join('')}
          </tr>
          ${machines.map(machine => `
            <tr>
              <td class="machine-header"><strong>${machine.code}</strong><br/>${machine.description || ''}</td>
              ${weekDates.map((_, dayIndex) => {
                const key = `${machine.id}-${dayIndex}`;
                const dayOrders = plannedOrders[key] || [];
                const totalHours = calculateDayLoad(machine.id, dayIndex);
                return `
                  <td>
                    ${dayOrders.map(orderId => {
                      const order = orders.find(o => o.id === orderId);
                      const part = order ? getPartById(order.part_id) : null;
                      return order ? `
                        <div class="order-item">
                          <strong>${order.code}</strong><br/>
                          ${part?.description || '-'}<br/>
                          <span class="hours">${calculateOrderTime(order).toFixed(1)}h</span>
                        </div>
                      ` : '';
                    }).join('')}
                    ${totalHours > 0 ? `<div class="hours"><strong>Total: ${totalHours.toFixed(1)}h</strong></div>` : ''}
                  </td>
                `;
              }).join('')}
            </tr>
          `).join('')}
        </table>
        <div class="footer">
          EP3D ENGINEERING PROJECT - Sistema de Gestão de Produção
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  // Exportar Planeamento Mensal para PDF
  const exportMonthlyToPDF = () => {
    const monthDays = getMonthDays(selectedMonth);
    const weeks = getWeeksInMonth(monthDays);
    
    // Construir o HTML das máquinas
    let machinesHtml = '';
    
    machines.forEach(machine => {
      let weeksHtml = '';
      
      weeks.forEach(week => {
        let daysHtml = '';
        
        week.forEach(day => {
          if (!day) {
            daysHtml += '<div class="day-cell day-other-month"></div>';
          } else {
            const isCurrentMonth = day.getMonth() === selectedMonth.getMonth();
            const dateStr = day.toISOString().split('T')[0];
            const planKey = `${machine.id}-${dateStr}`;
            
            // Buscar ordens para este dia
            const plannedOrderIds = monthlyPlanning[planKey] || [];
            const dayOrders = plannedOrderIds.map(id => orders.find(o => o.id === id)).filter(Boolean);
            
            let ordersHtml = dayOrders.slice(0, 3).map(order => {
              const cssClass = order.is_delayed ? 'delayed' : (order.status === 'running' ? 'running' : '');
              return `<span class="order-tag ${cssClass}">${order.code}</span>`;
            }).join('');
            
            if (dayOrders.length > 3) {
              ordersHtml += `<span class="more-orders">+${dayOrders.length - 3} mais</span>`;
            }
            
            daysHtml += `
              <div class="day-cell ${!isCurrentMonth ? 'day-other-month' : ''}">
                <div class="day-number">${day.getDate()}</div>
                ${ordersHtml}
              </div>
            `;
          }
        });
        
        weeksHtml += `<div class="week-row">${daysHtml}</div>`;
      });
      
      machinesHtml += `
        <div class="machine-section">
          <div class="machine-header">
            ${machine.code} - ${machine.description || ''}
          </div>
          <div class="calendar-grid">
            <div class="week-row">
              <div class="day-header">Seg</div>
              <div class="day-header">Ter</div>
              <div class="day-header">Qua</div>
              <div class="day-header">Qui</div>
              <div class="day-header">Sex</div>
              <div class="day-header">Sáb</div>
              <div class="day-header">Dom</div>
            </div>
            ${weeksHtml}
          </div>
        </div>
      `;
    });
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Planeamento Mensal - ${formatMonthYear(selectedMonth)}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 15px; font-size: 9px; }
          .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; border-bottom: 3px solid #4f46e5; padding-bottom: 10px; }
          .header-left { display: flex; align-items: center; gap: 15px; }
          .logo { height: 40px; }
          .header h1 { font-size: 16px; color: #4f46e5; }
          .header-right { text-align: right; }
          .header-right p { color: #64748b; font-size: 10px; }
          .machine-section { margin-bottom: 20px; page-break-inside: avoid; }
          .machine-header { background: #4f46e5; color: white; padding: 8px 12px; border-radius: 6px 6px 0 0; font-weight: bold; font-size: 11px; }
          .calendar-grid { border: 1px solid #e0e7ff; border-top: none; }
          .week-row { display: grid; grid-template-columns: repeat(7, 1fr); }
          .day-header { background: #e0e7ff; padding: 5px; text-align: center; font-weight: bold; color: #4f46e5; border-right: 1px solid #c7d2fe; font-size: 9px; }
          .day-header:last-child { border-right: none; }
          .day-cell { min-height: 50px; padding: 3px; border-right: 1px solid #e0e7ff; border-bottom: 1px solid #e0e7ff; vertical-align: top; }
          .day-cell:last-child { border-right: none; }
          .day-number { font-weight: bold; font-size: 9px; color: #6366f1; margin-bottom: 2px; }
          .day-other-month { background: #f8fafc; }
          .day-other-month .day-number { color: #94a3b8; }
          .order-tag { background: #dbeafe; color: #1e40af; padding: 1px 3px; border-radius: 2px; font-size: 7px; margin: 1px 0; display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .order-tag.delayed { background: #fee2e2; color: #991b1b; }
          .order-tag.running { background: #dcfce7; color: #166534; }
          .more-orders { font-size: 6px; color: #6366f1; font-style: italic; }
          .footer { margin-top: 15px; text-align: center; font-size: 9px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 10px; }
          .legend { display: flex; gap: 15px; justify-content: center; margin-top: 10px; font-size: 8px; }
          .legend-item { display: flex; align-items: center; gap: 4px; }
          .legend-color { width: 12px; height: 12px; border-radius: 2px; }
          @media print { 
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .machine-section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <img src="${LOGO_URL}" alt="Logo" class="logo" />
            <h1>Planeamento Mensal</h1>
          </div>
          <div class="header-right">
            <p><strong>Mês:</strong> ${formatMonthYear(selectedMonth)}</p>
            <p>Gerado: ${new Date().toLocaleString("pt-PT")}</p>
          </div>
        </div>
        
        ${machinesHtml}
        
        <div class="footer">
          <div class="legend">
            <div class="legend-item">
              <div class="legend-color" style="background: #dbeafe;"></div>
              <span>Pendente</span>
            </div>
            <div class="legend-item">
              <div class="legend-color" style="background: #dcfce7;"></div>
              <span>A Produzir</span>
            </div>
            <div class="legend-item">
              <div class="legend-color" style="background: #fee2e2;"></div>
              <span>Atrasada</span>
            </div>
          </div>
          <p style="margin-top: 10px;">EP3D ENGINEERING PROJECT - Sistema de Gestão de Produção</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planeamento-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Planeamento de Produção
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestão visual semanal • Otimização por material e cor
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Auto-refresh indicator */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/50 px-3 py-1.5 rounded-full">
            {isRefreshing ? (
              <>
                <RefreshCw className="h-3 w-3 animate-spin text-primary" />
                <span>A atualizar...</span>
              </>
            ) : (
              <>
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                <span>Auto-refresh: {nextRefresh}s</span>
              </>
            )}
          </div>
          <Button 
            variant="outline" 
            onClick={checkDelays}
            disabled={checkingDelays}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="check-delays-btn"
          >
            {checkingDelays ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <AlertTriangle className="h-4 w-4 mr-2" />
            )}
            Verificar Atrasos
          </Button>
          <Button 
            variant="outline" 
            onClick={autoPlanAll}
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="auto-plan-btn"
          >
            <Zap className="h-4 w-4 mr-2" />
            Planear Automatico
          </Button>
          <Button variant="outline" onClick={exportToPDF}>
            <Download className="h-4 w-4 mr-2" />
            Exportar PDF
          </Button>
          <Button onClick={savePlanning}>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Guardar Planeamento
          </Button>
        </div>
      </div>

      {/* Factory Profile Stats */}
      {factoryStats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="border-border">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Factory className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{factoryStats.totalMachines}</p>
                  <p className="text-xs text-muted-foreground">Máquinas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Zap className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{factoryStats.activeMachines}</p>
                  <p className="text-xs text-muted-foreground">Ativas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{factoryStats.totalOperators}</p>
                  <p className="text-xs text-muted-foreground">Operadores</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{factoryStats.pendingOrders}</p>
                  <p className="text-xs text-muted-foreground">Por Planear</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{factoryStats.runningOrders}</p>
                  <p className="text-xs text-muted-foreground">Em Produção</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-100 rounded-lg">
                  <Package className="h-5 w-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{factoryStats.totalStock.toFixed(0)} kg</p>
                  <p className="text-xs text-muted-foreground">Stock MP</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Week Navigation */}
      <Card className="border-border">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => {
                const newDate = new Date(selectedWeek);
                newDate.setDate(newDate.getDate() - 7);
                setSelectedWeek(newDate);
              }}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Semana Anterior
            </Button>
            <div className="text-center">
              <h2 className="text-xl font-bold text-foreground">
                Semana {formatDate(weekDates[0])} - {formatDate(weekDates[5])}
              </h2>
              <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                <Move className="h-4 w-4" />
                Arraste as ordens para reorganizar • Clique para adicionar
              </p>
              {schedulerStatus && (
                <div className="mt-2 flex items-center justify-center gap-2 text-xs">
                  <div className={`h-2 w-2 rounded-full ${schedulerStatus.scheduler_active ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-muted-foreground">
                    Verificação automática: {schedulerStatus.scheduler_active ? 'Ativa' : 'Inativa'} 
                    {schedulerStatus.scheduler_active && ` (próxima às ${schedulerStatus.check_hour}:00 UTC)`}
                  </span>
                </div>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => {
                const newDate = new Date(selectedWeek);
                newDate.setDate(newDate.getDate() + 7);
                setSelectedWeek(newDate);
              }}
            >
              Semana Seguinte
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Planning Grid with Drag & Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <Card className="border-border overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <CardTitle className="font-chivo flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Quadro de Planeamento Visual (Kanban)
              <Badge className="bg-white/20 text-white ml-2">
                <Move className="h-3 w-3 mr-1" />
                Drag & Drop
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="p-3 text-left font-semibold text-gray-700 w-40 border-r">
                      Máquina
                    </th>
                    {weekDates.map((date, idx) => (
                      <th key={idx} className="p-3 text-center font-semibold text-gray-700 border-r min-w-[150px]">
                        <div className="text-sm">{WEEKDAYS[idx]}</div>
                        <div className="text-xs text-gray-500">{formatDate(date)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {machines.map((machine) => (
                    <tr key={machine.id} className="border-b hover:bg-gray-50/50">
                      <td className="p-3 border-r bg-gray-50">
                        <div className="font-bold text-gray-800">{machine.code}</div>
                        <div className="text-xs text-gray-500">{machine.description}</div>
                        <Badge 
                          className={`mt-1 text-xs ${
                            machine.status === 'running' ? 'bg-green-500' :
                            machine.status === 'idle' ? 'bg-amber-500' :
                            machine.status === 'maintenance' ? 'bg-purple-500' :
                            'bg-gray-500'
                          }`}
                        >
                          {machine.status === 'running' ? 'A Produzir' :
                           machine.status === 'idle' ? 'Parada' :
                           machine.status === 'maintenance' ? 'Manutenção' :
                           machine.status}
                        </Badge>
                      </td>
                      {weekDates.map((_, dayIndex) => {
                        const key = `${machine.id}-${dayIndex}`;
                        const dayOrders = plannedOrders[key] || [];
                        const totalHours = calculateDayLoad(machine.id, dayIndex);
                        
                        return (
                          <DroppableCell
                            key={dayIndex}
                            machineId={machine.id}
                            dayIndex={dayIndex}
                            totalHours={totalHours}
                            isOver={activeId && !activeId.startsWith(`${machine.id}-${dayIndex}`)}
                          >
                            <div 
                              className="space-y-1 min-h-[80px] cursor-pointer"
                              onClick={() => {
                                setSelectedSlot({ machineId: machine.id, dayIndex, machineName: machine.code });
                                setDialogOpen(true);
                              }}
                            >
                              {dayOrders.map((orderId) => {
                                const order = orders.find(o => o.id === orderId);
                                if (!order) return null;
                                const part = getPartById(order.part_id);
                                const hours = calculateOrderTime(order);
                                
                                return (
                                  <DraggableOrderCard
                                    key={orderId}
                                    order={order}
                                    part={part}
                                    hours={hours}
                                    machineId={machine.id}
                                    dayIndex={dayIndex}
                                    onRemove={() => handleRemoveFromPlanning(machine.id, dayIndex, orderId)}
                                  />
                                );
                              })}
                              {dayOrders.length === 0 && (
                                <div className="flex items-center justify-center h-20 text-gray-400">
                                  <Plus className="h-5 w-5" />
                                </div>
                              )}
                            </div>
                            {totalHours > 0 && (
                              <div className={`text-xs font-bold mt-2 pt-1 border-t flex items-center justify-between ${
                                totalHours > 10 ? 'text-red-600 bg-red-50' : 
                                totalHours > 8 ? 'text-amber-600 bg-amber-50' : 
                                'text-green-600'
                              } ${totalHours > 8 ? 'p-1 rounded -mx-1' : ''}`}>
                                <span>Total: {totalHours.toFixed(1)}h</span>
                                {totalHours > 10 && <AlertTriangle className="h-4 w-4 animate-pulse" title="Sobrecarga crítica!" />}
                                {totalHours > 8 && totalHours <= 10 && <AlertTriangle className="h-3 w-3" title="Excede 8h" />}
                                {dayOrders.length > 1 && <span className="text-xs ml-1">({dayOrders.length} ordens)</span>}
                              </div>
                            )}
                          </DroppableCell>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Conflicts Legend */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
              <h4 className="text-sm font-bold text-gray-700 mb-2">Legenda:</h4>
              <div className="flex flex-wrap gap-4 text-xs">
                {/* Carga */}
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
                  <span className="text-gray-600">≤8h - Carga normal</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-amber-100 border border-amber-300"></div>
                  <span className="text-gray-600">8-10h - Carga elevada</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-100 border border-red-300 flex items-center justify-center">
                    <AlertTriangle className="h-2 w-2 text-red-600" />
                  </div>
                  <span className="text-gray-600">&gt;10h - Sobrecarga crítica</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">|</span>
                </div>
                {/* Estado das ordens */}
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded border-2 border-green-500 bg-green-50 flex items-center justify-center">
                    <Play className="h-2 w-2 text-green-600 fill-green-600" />
                  </div>
                  <span className="text-gray-600">A produzir</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded border-2 border-amber-500 bg-amber-50 flex items-center justify-center">
                    <Pause className="h-2 w-2 text-amber-600 fill-amber-600" />
                  </div>
                  <span className="text-gray-600">Em pausa</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded border-2 border-red-500 bg-red-50 flex items-center justify-center">
                    <AlertCircle className="h-2 w-2 text-red-600" />
                  </div>
                  <span className="text-gray-600 font-bold text-red-600">ATRASADA</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 italic">
                Arraste ordens entre células para reorganizar • Clique em "Verificar Atrasos" para propagar automaticamente
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Drag Overlay - shows what's being dragged */}
        <DragOverlay>
          {activeDragData && (
            <div className="bg-white rounded-lg p-2 shadow-lg border-2 border-blue-500 opacity-90">
              <div className="font-mono font-bold text-xs text-blue-600">
                {activeDragData.order?.code}
              </div>
              <div className="text-xs text-gray-600">
                A mover...
              </div>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Monthly Planning View */}
      <Card className="border-border bg-gradient-to-br from-indigo-50 to-purple-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-chivo flex items-center gap-2 text-indigo-800">
              <Calendar className="h-5 w-5" />
              Quadro de Planeamento Mensal
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newMonth = new Date(selectedMonth);
                  newMonth.setMonth(newMonth.getMonth() - 1);
                  setSelectedMonth(newMonth);
                }}
                className="border-indigo-300"
              >
                <ChevronLeft className="h-4 w-4" />
                Mês Anterior
              </Button>
              <span className="text-lg font-bold capitalize text-indigo-800 min-w-[180px] text-center">
                {formatMonthYear(selectedMonth)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newMonth = new Date(selectedMonth);
                  newMonth.setMonth(newMonth.getMonth() + 1);
                  setSelectedMonth(newMonth);
                }}
                className="border-indigo-300"
              >
                Mês Seguinte
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={exportMonthlyToPDF}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 ml-4"
                data-testid="export-monthly-pdf-btn"
              >
                <Download className="h-4 w-4 mr-1" />
                Exportar PDF
              </Button>
            </div>
          </div>
          <p className="text-sm text-indigo-600 mt-1">
            Visão mensal baseada nas ordens de fabrico planeadas
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {machines.map((machine) => {
              const monthDays = getMonthDays(selectedMonth);
              const weeks = getWeeksInMonth(monthDays);
              
              return (
                <div key={machine.id} className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Factory className="h-4 w-4 text-indigo-600" />
                    <span className="font-bold text-indigo-800">{machine.code}</span>
                    <span className="text-sm text-indigo-600">{machine.description}</span>
                    <Badge className={machine.status === 'running' ? 'bg-green-500' : machine.status === 'maintenance' ? 'bg-amber-500' : 'bg-gray-400'}>
                      {machine.status === 'running' ? 'Ativa' : machine.status === 'maintenance' ? 'Manutenção' : 'Parada'}
                    </Badge>
                  </div>
                  
                  <div className="bg-white rounded-lg border border-indigo-200 overflow-hidden">
                    {/* Days header */}
                    <div className="grid grid-cols-7 bg-indigo-100 text-xs font-bold text-indigo-800">
                      {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map(day => (
                        <div key={day} className="p-2 text-center border-r border-indigo-200 last:border-r-0">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    {/* Calendar grid */}
                    {weeks.map((week, weekIdx) => (
                      <div key={weekIdx} className="grid grid-cols-7 border-t border-indigo-200">
                        {week.map((day, dayIdx) => {
                          const isCurrentMonth = day.getMonth() === selectedMonth.getMonth();
                          const isToday = day.toDateString() === new Date().toDateString();
                          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                          
                          // Get orders for this date and machine
                          const dateStr = day.toISOString().split('T')[0];
                          const planKey = `${machine.id}-${dateStr}`;
                          const plannedOrderIds = monthlyPlanning[planKey] || [];
                          const ordersFromDate = getOrdersForDate(day, machine.id);
                          
                          // Combine both sources
                          const allOrderIds = [...new Set([
                            ...plannedOrderIds,
                            ...ordersFromDate.map(o => o.id)
                          ])];
                          
                          return (
                            <div
                              key={dayIdx}
                              className={`min-h-[60px] p-1 border-r border-indigo-100 last:border-r-0 ${
                                !isCurrentMonth ? 'bg-gray-50 opacity-50' :
                                isToday ? 'bg-indigo-100' :
                                isWeekend ? 'bg-purple-50' : 'bg-white'
                              }`}
                            >
                              <div className={`text-xs font-bold mb-1 ${
                                isToday ? 'text-indigo-600' :
                                !isCurrentMonth ? 'text-gray-400' :
                                'text-gray-600'
                              }`}>
                                {day.getDate()}
                              </div>
                              
                              {/* Show orders */}
                              <div className="space-y-0.5">
                                {allOrderIds.slice(0, 3).map(orderId => {
                                  const order = orders.find(o => o.id === orderId) || 
                                               (ordersFromDate.find(o => o.id === orderId));
                                  if (!order) return null;
                                  
                                  const part = getPartById(order.part_id);
                                  return (
                                    <div
                                      key={orderId}
                                      className="text-xs px-1 py-0.5 rounded bg-blue-100 text-blue-800 truncate cursor-pointer hover:bg-blue-200"
                                      title={`${order.code} - ${part?.description || ''}`}
                                      onClick={() => {
                                        // Navigate to the week of this order
                                        const weekStart = getWeekStart(day);
                                        setSelectedWeek(weekStart);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }}
                                    >
                                      {order.code}
                                    </div>
                                  );
                                })}
                                {allOrderIds.length > 3 && (
                                  <div className="text-xs text-indigo-600 font-bold">
                                    +{allOrderIds.length - 3}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-indigo-100 border border-indigo-300"></div>
              <span className="text-gray-600">Hoje</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-purple-50 border border-purple-200"></div>
              <span className="text-gray-600">Fim de semana</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-100 border border-blue-200"></div>
              <span className="text-gray-600">Ordem planeada</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-indigo-600">
              <span>💡 Clique numa ordem para ir à vista semanal</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders to Plan - Sorted by Material and Color */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
            <Box className="h-5 w-5" />
            Ordens Por Planear ({orders.length})
            <span className="text-sm font-normal text-muted-foreground ml-2">
              Ordenadas por material e cor para reduzir purgas
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p>Todas as ordens estão planeadas!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ordem</TableHead>
                    <TableHead>Peça</TableHead>
                    <TableHead>Material</TableHead>
                    <TableHead>Cor</TableHead>
                    <TableHead>Molde</TableHead>
                    <TableHead className="text-right">Quantidade</TableHead>
                    <TableHead className="text-right">Tempo Est.</TableHead>
                    <TableHead>Sequência</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getSortedOrders().map((order, index) => {
                    const part = getPartById(order.part_id);
                    const mold = getMoldById(order.mold_id);
                    const hours = calculateOrderTime(order);
                    
                    return (
                      <TableRow key={order.id} className="hover:bg-blue-50/50">
                        <TableCell className="font-mono font-bold">{order.code}</TableCell>
                        <TableCell>{part?.description || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {part?.material || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Palette className="h-4 w-4 text-gray-400" />
                            {part?.color || 'Natural'}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono">{mold?.code || '-'}</TableCell>
                        <TableCell className="text-right font-mono">
                          {order.good_quantity_target?.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {hours.toFixed(1)}h
                        </TableCell>
                        <TableCell>
                          <Badge className={`${
                            index === 0 ? 'bg-green-500' :
                            index < 3 ? 'bg-blue-500' :
                            'bg-gray-500'
                          }`}>
                            #{index + 1}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sequencing Tips */}
      <Card className="border-border bg-gradient-to-br from-amber-50 to-orange-50">
        <CardHeader>
          <CardTitle className="font-chivo flex items-center gap-2 text-amber-800">
            <Settings className="h-5 w-5" />
            Dicas de Sequenciamento (Redução de Purgas)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                <Package className="h-4 w-4" />
                Sequência de Materiais
              </h4>
              <div className="flex flex-wrap gap-1">
                {MATERIAL_ORDER.slice(0, 8).map((mat, idx) => (
                  <span key={mat} className="inline-flex items-center">
                    <Badge variant="outline" className="bg-white">{mat}</Badge>
                    {idx < 7 && <ArrowRight className="h-3 w-3 mx-1 text-amber-600" />}
                  </span>
                ))}
              </div>
              <p className="text-xs text-amber-700 mt-2">
                Sequenciar do mais fácil para o mais difícil de purgar
              </p>
            </div>
            <div>
              <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                <Palette className="h-4 w-4" />
                Sequência de Cores
              </h4>
              <div className="flex flex-wrap gap-1">
                {COLOR_ORDER.slice(0, 6).map((color, idx) => (
                  <span key={color} className="inline-flex items-center">
                    <Badge variant="outline" className="bg-white capitalize">{color}</Badge>
                    {idx < 5 && <ArrowRight className="h-3 w-3 mx-1 text-amber-600" />}
                  </span>
                ))}
              </div>
              <p className="text-xs text-amber-700 mt-2">
                Cores claras → escuras para evitar contaminação
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Order Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-chivo">
              Adicionar Ordem ao Planeamento
            </DialogTitle>
            <DialogDescription>
              {selectedSlot && `${selectedSlot.machineName} - ${WEEKDAYS[selectedSlot.dayIndex]} (${formatDate(weekDates[selectedSlot.dayIndex])})`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[400px] overflow-y-auto">
            {getSortedOrders().map((order) => {
              const part = getPartById(order.part_id);
              const mold = getMoldById(order.mold_id);
              const hours = calculateOrderTime(order);
              const isAlreadyPlanned = Object.values(plannedOrders).flat().includes(order.id);
              
              return (
                <div 
                  key={order.id}
                  className={`p-3 rounded-lg border ${
                    isAlreadyPlanned 
                      ? 'bg-gray-100 border-gray-300 opacity-50' 
                      : 'bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50 cursor-pointer'
                  }`}
                  onClick={() => {
                    if (!isAlreadyPlanned && selectedSlot) {
                      handleAddToPlanning(selectedSlot.machineId, selectedSlot.dayIndex, order.id);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-mono font-bold text-blue-600">{order.code}</div>
                      <div className="text-sm text-gray-600">{part?.description || '-'}</div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline">{part?.material || '-'}</Badge>
                      <div className="text-xs text-gray-500 mt-1">{hours.toFixed(1)}h</div>
                    </div>
                  </div>
                  {isAlreadyPlanned && (
                    <div className="text-xs text-amber-600 mt-2">
                      ⚠️ Já planeada
                    </div>
                  )}
                </div>
              );
            })}
            {orders.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                Não há ordens pendentes
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Ordens Atrasadas */}
      <Dialog open={delayDialogOpen} onOpenChange={setDelayDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-chivo flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Ordens Atrasadas ({delayedOrders.length})
            </DialogTitle>
            <DialogDescription>
              As seguintes ordens ultrapassaram a data de fim planeada
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[500px] overflow-y-auto">
            {delayedOrders.map((item) => {
              const machine = machines.find(m => m.id === item.machine_id);
              return (
                <div 
                  key={item.order_id}
                  className="p-4 rounded-lg border-2 border-red-300 bg-red-50"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-mono font-bold text-red-600 text-lg">
                        {item.order_code}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        <strong>Máquina:</strong> {machine?.code || item.machine_id}
                      </div>
                      <div className="text-sm text-gray-600">
                        <strong>Data Fim Planeado:</strong> {item.planned_end}
                      </div>
                      <div className="text-sm text-gray-600">
                        <strong>Estado:</strong>{' '}
                        <Badge className={
                          item.status === 'running' ? 'bg-green-500' :
                          item.status === 'paused' ? 'bg-amber-500' :
                          'bg-gray-500'
                        }>
                          {item.status === 'running' ? 'A Produzir' :
                           item.status === 'paused' ? 'Em Pausa' :
                           item.status === 'pending' ? 'Pendente' :
                           item.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-red-600">
                        -{item.delay_days}
                      </div>
                      <div className="text-xs text-red-500 font-medium">
                        dia(s) de atraso
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 p-2 bg-white rounded border border-red-200">
                    <p className="text-xs text-gray-600">
                      <strong>Ação recomendada:</strong> As ordens subsequentes nesta máquina foram automaticamente reagendadas. 
                      Verifique a capacidade e considere horas extra ou redistribuição de trabalho.
                    </p>
                  </div>
                </div>
              );
            })}
            {delayedOrders.length === 0 && (
              <div className="text-center py-8 text-green-600">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-3" />
                <p className="font-medium">Todas as ordens estão dentro do prazo!</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDelayDialogOpen(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
